<template>
  <div class="home-view">
    <!-- Hero Section -->
    <section class="hero-section">
      <div class="hero-content">
        <h1>Attractive Unisex Salon – Beauty &amp; Style in Gondia</h1>
        <p class="hero-subtitle">Your journey to a more confident and attractive you begins here.</p>
        <router-link to="/services" class="btn btn-primary btn-large">Explore Our Services</router-link>
        <router-link to="/book-appointment" class="btn btn-secondary btn-large">Book Appointment</router-link>
      </div>
    </section>

    <!-- About Us Snippet -->
    <section class="about-snippet-section section-padding">
      <div class="container">
        <h2 class="section-title">Welcome to Attractive Unisex Salon</h2>
        <p>
          Step into a world of relaxation and transformation. At Attractive Unisex Salon, 
          we are dedicated to providing top-notch hair, skin, and beauty services 
          tailored to your unique style and preferences. Our team of experienced professionals 
          is passionate about making you look and feel your best.
        </p>
        <router-link to="/about" class="btn btn-outline">Learn More About Us</router-link>
      </div>
    </section>

    <!-- Services Highlight -->
    <section class="services-highlight-section section-padding">
      <div class="container">
        <h2 class="section-title">Our Popular Services</h2>
        <div class="services-grid">
          <div class="service-card">
            <div class="service-card-image-container">
              <img src="@/assets/placeholder/haircut.jpg" alt="Professional haircut and styling at Attractive Salon Gondia" loading="lazy" width="400" height="300" />
            </div>
            <div class="service-card-content">
              <h3>Hair Styling & Cuts</h3>
              <p>Modern cuts, vibrant colors, and elegant styling for all occasions.</p>
            </div>
          </div>
          <div class="service-card">
            <div class="service-card-image-container">
              <img src="@/assets/placeholder/skincare.jpg" alt="Revitalizing skincare facial treatment at Attractive Salon Gondia" loading="lazy" width="400" height="300" />
            </div>
            <div class="service-card-content">
              <h3>Revitalizing Skin Care</h3>
              <p>Facials, treatments, and therapies to rejuvenate your skin.</p>
            </div>
          </div>
          <div class="service-card">
            <div class="service-card-image-container">
              <img src="@/assets/placeholder/bridal.jpg" alt="Bridal and special occasion makeup at Attractive Salon Gondia" loading="lazy" width="400" height="300" />
            </div>
            <div class="service-card-content">
              <h3>Bridal & Special Occasions</h3>
              <p>Look stunning on your special day with our expert makeup artists.</p>
            </div>
          </div>
        </div>
        <router-link to="/services" class="btn btn-primary">View All Services</router-link>
      </div>
    </section>

    <!-- Testimonials Section (Placeholder) -->
    <section class="testimonials-section section-padding">
      <div class="container">
        <h2 class="section-title">What Our Clients Say</h2>
        <div class="testimonial-grid">
          <div class="testimonial-card">
            <p>"Absolutely loved my new haircut! The stylists are so talented and friendly. Highly recommend!"</p>
            <h4>- Priya S.</h4>
          </div>
          <div class="testimonial-card">
            <p>"The best facial I've ever had. My skin feels amazing. Will definitely be back."</p>
            <h4>- Rohan M.</h4>
          </div>
          <div class="testimonial-card">
            <p>"Got my bridal makeup done here and it was flawless. They made me feel like a princess!"</p>
            <h4>- Aisha K.</h4>
          </div>
        </div>
      </div>
    </section>

    <!-- Special Offers Section -->
    <section class="offers-section section-padding">
      <div class="container">
        <h2 class="section-title">Current Offers</h2>
        <div class="offers-carousel-wrapper">
          <button v-if="offers.length > 3" @click="scrollOffers(-1)" class="carousel-control prev" aria-label="Previous Offer">←</button>
          <div class="offers-carousel" :class="{ 'is-centered': offers.length <= 3 }" ref="offersCarousel">
            <OfferCard v-for="offer in offers" :key="offer.id" :offer="offer" />
          </div>
          <button v-if="offers.length > 3" @click="scrollOffers(1)" class="carousel-control next" aria-label="Next Offer">→</button>
        </div>
      </div>
    </section>

    <!-- Contact and Map Section -->
    <section class="contact-map-section section-padding">
      <div class="container">
        <h2 class="section-title">Visit Us or Get in Touch</h2>
        <div class="contact-map-grid">
          <div class="contact-details">
            <h3>Our Address</h3>
            <p>
              Attractive Unisex Salon and Academy<br />
              Near Ayush Hospital, Rail Toly,<br />
              Gondia, Maharashtra 441614, India
            </p>
            <h3>Contact Us</h3>
            <p>
              Phone: <a href="tel:+918446607478">+91-844-660-7478</a> <br />
              Email: <a href="mailto:attractiveunisexsalon@gmail.com">attractiveunisexsalon@gmail.com</a>
            </p>
          </div>
          <div class="map-container">
            <iframe 
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1402.7598485247859!2d80.19206499929479!3d21.46434031933013!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a2baf46f01b6aa3%3A0x8d02e3d2cc378918!2sThe%20Attractive%20Unisex%20salon%20and%20Academy!5e0!3m2!1sen!2sin!4v1750080347192!5m2!1sen!2sin" 
              width="100%" 
              height="350" 
              style="border:0;" 
              allowfullscreen 
              loading="lazy" 
              referrerpolicy="no-referrer-when-downgrade">
            </iframe>
          </div>
        </div>
      </div>
    </section>

    <!-- Call to Action / Booking Snippet -->
    <section class="cta-section section-padding">
      <div class="container">
        <h2 class="section-title-light">Ready for a Makeover?</h2>
        <p>Contact us today to schedule your appointment or consult with our beauty experts.</p>
        <router-link to="/book-appointment" class="btn btn-gold btn-large">Book Your Appointment Now</router-link>
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, Ref } from 'vue';
import OfferCard, { Offer } from '@/components/OfferCard.vue'; // Import OfferCard and Offer interface
import offerService from '@/services/offer.service'; // Import your offerService

export default defineComponent({
  name: 'HomeView',
  components: {
    OfferCard,
  },
  setup() {
    const offers: Ref<Offer[]> = ref([]);
    const offersCarousel: Ref<HTMLElement | null> = ref(null);

    const fetchOffers = async () => {
      try {
        const response = await offerService.getActiveOffers();
        if (Array.isArray(response)) {
          offers.value = response.map(offer => ({
            id: offer.id,
            title: offer.title,
            description: offer.description,
            imageUrl: offer.imageUrl,
            imageName: 'salon-hero.jpg', // Placeholder image
            linkUrl: `/offer/${offer.promoCode}` // Example link
          }));
        } else {
          console.error('Failed to fetch offers: response is not an array', response);
        }
      } catch (error) {
        console.error('Failed to fetch offers:', error);
        // Potentially set a default or error state for offers
      }
    };

    const scrollOffers = (direction: number) => {
      if (offersCarousel.value) {
        const scrollAmount = 320; // Width of OfferCard (300) + margin-right (20)
        offersCarousel.value.scrollBy({ left: scrollAmount * direction, behavior: 'smooth' });
      }
    };

    onMounted(() => {
      fetchOffers();
    });

    return {
      offers,
      offersCarousel,
      scrollOffers,
    };
  },
});
</script>

<style scoped>
.home-view {
  width: 100%;
}

/* Hero Section */
.hero-section {
  background-image: linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url('@/assets/placeholder/salon-hero.jpg'); /* Darker overlay */
  background-size: cover;
  background-position: center;
  color: var(--text-light, #fff);
  padding: 120px 20px; /* Increased padding */
  text-align: center;
  min-height: 75vh; /* Slightly taller */
  display: flex;
  flex-direction: column; /* Align items vertically */
  align-items: center;
  justify-content: center;
}

.hero-content h1 {
  font-size: 3.8em; /* Slightly larger */
  margin-bottom: 0.4em;
  font-weight: 700; /* Bolder */
  color: var(--brand-gold, #DAA520);
  text-shadow: 1px 1px 3px rgba(0,0,0,0.3);
}

.hero-subtitle { /* Class for hero paragraph */
  font-size: 1.5em; /* Slightly larger */
  margin-bottom: 2em; /* More space before buttons */
  max-width: 650px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.5;
  opacity: 0.9;
}

/* Common Container */
.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 50px 20px; /* Consistent padding */
  text-align: center;
}

/* Section Base Styles */
.section-padding { /* Replaces individual padding for sections */
  padding: 80px 20px;
}

.about-snippet-section {
  background-color: var(--background-light, #fff);
  color: var(--text-dark, #333);
}

.services-highlight-section {
  background-color: #f8f8f8; /* Slightly different light grey */
}

.offers-section {
  background-color: var(--background-light, #fff); /* Or a very subtle off-white like #fafafa */
}

.testimonials-section {
  background-color: var(--brand-gold, #DAA520);
  color: var(--brand-black, #1a1a1a);
}

.contact-map-section {
  background-color: #f0f0f0; /* A very light grey, different from services */
}

.cta-section {
  background-color: var(--brand-black, #1a1a1a);
  color: var(--text-light, #fff);
}

.section-title {
  font-size: 2.8em; /* Larger section titles */
  margin-bottom: 1em; /* More space after title */
  font-weight: 600;
  color: var(--brand-black, #333);
}
.testimonials-section .section-title {
  color: var(--brand-black, #1a1a1a); /* Dark title on gold background */
}

.section-title-light { /* For dark backgrounds */
  font-size: 2.8em;
  margin-bottom: 1em;
  font-weight: 600;
  color: var(--brand-gold, #DAA520);
}


.about-snippet-section p, .cta-section p { /* Keep services p separate for card styling */
  font-size: 1.15em; /* Slightly larger paragraph text */
  line-height: 1.8;
  max-width: 800px;
  margin: 0 auto 25px auto; /* More space after paragraphs */
}

/* Services Grid */
.services-grid {
  display: grid; /* Using grid for more robust layout */
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); /* Responsive columns */
  gap: 30px;
  justify-content: center;
  margin-bottom: 40px;
}

.service-card {
  background-color: #fff;
  border-radius: 10px; /* Slightly more rounded */
  box-shadow: 0 5px 20px rgba(0,0,0,0.08); /* Softer shadow */
  overflow: hidden;
  text-align: left;
  transition: transform 0.3s ease, box-shadow 0.4s ease;
  display: flex;
  flex-direction: column;
}

.service-card:hover {
  transform: translateY(-8px); /* More lift on hover */
  box-shadow: 0 10px 30px rgba(0,0,0,0.12);
}

.service-card-image-container {
  width: 100%;
  height: 220px; /* Slightly taller images */
  overflow: hidden;
}
.service-card img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.4s ease;
}
.service-card:hover img {
  transform: scale(1.05); /* Subtle zoom on image hover */
}

.service-card-content {
  padding: 20px 25px; /* More padding in content */
  flex-grow: 1; /* Allows content to fill space if cards have different text lengths */
}

.service-card h3 {
  font-size: 1.6em; /* Larger service titles */
  color: var(--brand-gold, #DAA520);
  margin-top: 0;
  margin-bottom: 10px;
  font-weight: 600;
}

.service-card p {
  font-size: 1em;
  color: #555;
  line-height: 1.6;
}

/* Offers Carousel */
.offers-carousel-wrapper {
  position: relative;
  margin-top: 30px;
}

.offers-carousel {
  display: flex;
  overflow-x: auto; /* Allows horizontal scrolling */
  scroll-behavior: smooth;
  padding-bottom: 20px; /* Space for scrollbar if it appears, or just visual padding */
  -ms-overflow-style: none;  /* IE and Edge */
  scrollbar-width: none;  /* Firefox */
}
.offers-carousel::-webkit-scrollbar { /* Chrome, Safari, Opera */
  display: none;
}

.offers-carousel.is-centered {
  justify-content: center;
}

.carousel-control {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  font-size: 1.5em;
  cursor: pointer;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.3s ease;
}
.carousel-control:hover {
  background-color: rgba(0, 0, 0, 0.8);
}
.carousel-control.prev {
  left: -15px; /* Adjust positioning */
}
.carousel-control.next {
  right: -15px; /* Adjust positioning */
}


/* Testimonials Grid */
.testimonial-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
  margin-top: 30px;
}

.testimonial-card {
  background-color: rgba(255,255,255,0.15); /* Semi-transparent white on gold */
  padding: 25px;
  border-radius: 8px;
  text-align: left;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
}
.testimonial-card p {
  font-size: 1.05em;
  line-height: 1.7;
  margin-bottom: 15px;
  font-style: italic;
  color: var(--brand-black);
}
.testimonial-card h4 {
  font-size: 1.1em;
  font-weight: bold;
  color: var(--brand-black);
  text-align: right;
  margin: 0;
}

/* Contact & Map Section */
.contact-map-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  align-items: flex-start; /* Align items to the top */
  text-align: left;
  margin-top: 30px;
}
.contact-details h3 {
  font-size: 1.6em;
  color: var(--brand-gold);
  margin-top: 0;
  margin-bottom: 10px;
}
.contact-details p {
  font-size: 1.05em;
  line-height: 1.8;
  margin-bottom: 20px;
  color: var(--text-dark);
}
.contact-details p:last-child {
  margin-bottom: 0;
}
.contact-details a {
  color: var(--brand-gold);
  text-decoration: none;
  transition: color 0.3s ease;
}
.contact-details a:hover {
  color: var(--brand-black);
}
.map-container {
  border-radius: 8px;
  overflow: hidden; /* To clip the iframe corners if needed */
  box-shadow: 0 5px 15px rgba(0,0,0,0.1);
}
.map-container iframe {
  display: block; /* Removes extra space below iframe */
}


/* Buttons */
.btn {
  padding: 12px 30px; /* Slightly more horizontal padding */
  text-decoration: none;
  border-radius: 6px; /* Slightly more rounded buttons */
  font-weight: bold;
  font-size: 1.05em; /* Slightly larger button text */
  transition: all 0.3s ease; /* Smoother transition for all properties */
  display: inline-block;
  margin: 10px 5px; /* More vertical margin for buttons */
  cursor: pointer;
  border: 2px solid transparent;
  text-transform: uppercase; /* Uppercase button text */
  letter-spacing: 0.5px;
}
.btn-large {
  padding: 15px 35px;
  font-size: 1.1em;
}

.btn-primary {
  background-color: var(--brand-gold, #DAA520);
  color: var(--brand-black, #1a1a1a);
  border-color: var(--brand-gold, #DAA520);
}
.btn-primary:hover {
  background-color: #c7921e; /* Darker gold */
  border-color: #c7921e;
  transform: translateY(-2px);
  box-shadow: 0 4px 10px rgba(0,0,0,0.1);
}

.btn-secondary { /* New secondary button style for hero */
  background-color: transparent;
  color: var(--text-light, #fff);
  border-color: var(--text-light, #fff);
}
.btn-secondary:hover {
  background-color: var(--text-light, #fff);
  color: var(--brand-black, #1a1a1a);
  transform: translateY(-2px);
  box-shadow: 0 4px 10px rgba(255,255,255,0.1);
}


.btn-outline {
  background-color: transparent;
  color: var(--brand-gold, #DAA520);
  border-color: var(--brand-gold, #DAA520);
}
.btn-outline:hover {
  background-color: var(--brand-gold, #DAA520);
  color: var(--brand-black, #1a1a1a);
  transform: translateY(-2px);
}

.btn-gold { /* For dark backgrounds */
  background-color: var(--brand-gold, #DAA520);
  color: var(--brand-black, #1a1a1a);
  border-color: var(--brand-gold, #DAA520);
}
.btn-gold:hover {
  background-color: #fff;
  color: var(--brand-gold, #DAA520);
  border-color: #fff;
  transform: translateY(-2px);
  box-shadow: 0 4px 10px rgba(255,255,255,0.1);
}

/* Responsive adjustments */
@media (max-width: 992px) {
  /* Service cards already handled by grid auto-fit */
  .testimonial-grid {
    grid-template-columns: 1fr; /* Stack testimonials on smaller screens */
  }
  .contact-map-grid {
    grid-template-columns: 1fr; /* Stack contact details and map */
    gap: 30px;
  }
  .map-container {
    margin-top: 20px; /* Add some space above map when stacked */
  }
  .carousel-control.prev {
    left: 5px;
  }
  .carousel-control.next {
    right: 5px;
  }
}

@media (max-width: 768px) {
  .hero-content h1 {
    font-size: 2.8em; /* Adjusted for tablet */
  }
  .hero-subtitle {
    font-size: 1.3em;
  }
  .section-title, .section-title-light {
    font-size: 2.2em;
  }
  .btn-large {
    padding: 12px 25px;
    font-size: 1em;
  }
}

@media (max-width: 576px) {
  .hero-section {
    padding: 80px 20px;
    min-height: 60vh;
  }
  .hero-content h1 {
    font-size: 2.2em;
  }
  .hero-subtitle {
    font-size: 1.1em;
  }
  .section-title, .section-title-light {
    font-size: 1.8em;
  }
  .services-grid, .testimonial-grid {
    grid-template-columns: 1fr; /* Stack cards */
  }
  .btn {
    padding: 10px 20px;
    font-size: 0.95em;
    width: calc(100% - 20px); /* Full width buttons on mobile */
    max-width: 300px;
    margin-left: auto;
    margin-right: auto;
    display: block;
  }
  .hero-content .btn { /* Keep hero buttons inline-block */
    width: auto; 
    display: inline-block;
    margin: 10px 5px;
  }
  .hero-content .btn-large {
    padding: 12px 20px;
    font-size: 1em;
  }
}
</style>